import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { ProjectAggregate } from '@/features/reports/utils/aggregateTasks';
import { formatDurationHms } from '@/utils/formatters';

interface ProjectDonutChartProps {
  data: ProjectAggregate[];
  totalMinutes: number;
}

export function ProjectDonutChart({ data, totalMinutes }: ProjectDonutChartProps) {
  const fallbackColor = 'hsl(var(--primary))';

  return (
    <div data-testid="project-donut-chart" className="relative h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="hours"
            nameKey="projectName"
            innerRadius="60%"
            outerRadius="85%"
            paddingAngle={2}
            stroke="none"
            isAnimationActive={false}
          >
            {data.map((entry, index) => (
              <Cell key={`slice-${entry.projectId}-${index}`} fill={entry.color ?? fallbackColor} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || payload.length === 0) return null;
              const item = payload[0].payload as ProjectAggregate;
              const totalHours = totalMinutes / 60;
              const percent = totalHours > 0 ? Math.round((item.hours / totalHours) * 100) : 0;
              return (
                <div className="rounded-md border bg-popover px-3 py-2 text-sm shadow-sm">
                  <p className="font-medium text-foreground">{item.projectName}</p>
                  <p className="text-muted-foreground">
                    {formatDurationHms(Math.round(item.hours * 60))} ({percent}%)
                  </p>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-sm text-muted-foreground">Total</p>
        <p className="text-2xl font-semibold text-foreground" data-testid="donut-total-duration">
          {formatDurationHms(totalMinutes)}
        </p>
      </div>
    </div>
  );
}
